/**
 * The scoped SharePoint client.
 *
 * The rule this file exists to enforce: **every read is filtered to one
 * project.** Not by convention, not by review — the only way to reach a list is
 * through a `ProjectScope`, and the scope writes `ProjectId eq N` into every
 * query itself. There is no method that returns rows from more than one project,
 * so an unfiltered query is not a bug you can write here.
 *
 * That matters because the failure is quiet. A view that widens to every project
 * shows MY02's breakers inside a new project's register, an engineer photographs
 * them, and nothing complains until a report is compiled against the wrong
 * equipment.
 *
 * Library rows are the one deliberate exception, and they are explicit:
 * `listWithLibrary` returns rows belonging to this project *or* to none. Shot
 * profiles work this way — what an ACB needs photographed is not a fact about a
 * client.
 */

import { odataString, SpError, type SpHttp } from './http.js';

/** Lists the platform reads. Kept here so a typo is a compile error, not an empty screen. */
export const LIST = {
  projects: 'Cx Projects',
  templates: 'Cx Templates',
  sections: 'Cx Template Sections',
  items: 'Cx Template Items',
  assets: 'Cx Assets',
  instances: 'Cx Instances',
  resultLines: 'Cx Result Lines',
  issues: 'Cx Issues',
  instruments: 'Cx Instruments',
  signoffs: 'Cx Signoffs',
  shotProfiles: 'Cx Shot Profiles',
  meterVerification: 'Cx Meter Verification',
  changeRequests: 'Cx Change Requests',
} as const;

export type ListName = (typeof LIST)[keyof typeof LIST];

/**
 * The only lists where a library row may be merged into a project's results.
 *
 * Checked against the tenant: asking for `(ProjectId eq 3 or ProjectId eq null)`
 * on Cx Template Items returns 190 rows — the sandbox's own 95 plus the
 * library's 95. That is correct for the filter and wrong for a script: a project
 * runs its own copy, and showing the library's alongside it would double every
 * check on the screen. So the merge is allowed by list, not by habit.
 */
const LIBRARY_LISTS: ReadonlySet<ListName> = new Set([LIST.shotProfiles]);

export interface Query {
  select?: readonly string[];
  /** ANDed with the project filter — it can narrow the scope, never widen it. */
  filter?: string;
  orderBy?: string;
  top?: number;
  expand?: readonly string[];
}

function listPath(list: ListName): string {
  return `web/lists/getbytitle(${encodeURIComponent(odataString(list))})`;
}

function buildQuery(parts: Query, scopeFilter: string): string {
  const qs: string[] = [];
  if (parts.select?.length) qs.push(`$select=${parts.select.join(',')}`);
  if (parts.expand?.length) qs.push(`$expand=${parts.expand.join(',')}`);
  // The scope filter is always present and always first. A caller's filter is
  // ANDed onto it inside parentheses, so `a or b` cannot escape the scope.
  const filter = parts.filter ? `${scopeFilter} and (${parts.filter})` : scopeFilter;
  qs.push(`$filter=${encodeURIComponent(filter)}`);
  if (parts.orderBy) qs.push(`$orderby=${encodeURIComponent(parts.orderBy)}`);
  qs.push(`$top=${parts.top ?? 500}`);
  return qs.join('&');
}

/**
 * Caches each list's `ListItemEntityTypeFullName`.
 *
 * Hand-building it from the title is a trap: `Cx Assets` becomes
 * `SP.Data.Cx_x0020_AssetsListItem`, and every space, ampersand and bracket has
 * its own encoding. Ask the list once and remember the answer.
 */
export class EntityTypes {
  private readonly cache = new Map<string, string>();
  constructor(private readonly http: SpHttp) {}

  async of(list: ListName): Promise<string> {
    const hit = this.cache.get(list);
    if (hit) return hit;
    const res = await this.http.get<{ ListItemEntityTypeFullName?: string }>(
      `${listPath(list)}?$select=ListItemEntityTypeFullName`,
    );
    const type = res.ListItemEntityTypeFullName;
    if (!type) throw new SpError(0, list, 'list has no ListItemEntityTypeFullName');
    this.cache.set(list, type);
    return type;
  }

  /** Test seam, and a way to warm the cache from a cached manifest. */
  prime(list: ListName, type: string): void {
    this.cache.set(list, type);
  }
}

export interface ProjectRef {
  id: number;
  code: string;
  name: string;
}

/** Everything the app does with list data happens through one of these. */
export class ProjectScope {
  private readonly scopeFilter: string;
  private readonly withLibraryFilter: string;

  constructor(
    private readonly http: SpHttp,
    private readonly types: EntityTypes,
    readonly project: ProjectRef,
  ) {
    if (!Number.isInteger(project.id) || project.id <= 0) {
      throw new SpError(0, 'scope', `a project scope needs a real project id, got ${String(project.id)}`);
    }
    this.scopeFilter = `ProjectId eq ${project.id}`;
    this.withLibraryFilter = `(ProjectId eq ${project.id} or ProjectId eq null)`;
  }

  /** Rows belonging to this project. Nothing else can come back. */
  async list<T>(list: ListName, q: Query = {}): Promise<T[]> {
    return this.http.getAll<T>(`${listPath(list)}/items?${buildQuery(q, this.scopeFilter)}`);
  }

  /**
   * Rows belonging to this project *or* to no project at all.
   *
   * Only for reference data a project may override — shot profiles today. Never
   * for equipment or results: a result line with no project is a bug, not a
   * shared default, and quietly including it would put another project's
   * reading on this project's report.
   */
  async listWithLibrary<T>(list: ListName, q: Query = {}): Promise<T[]> {
    if (!LIBRARY_LISTS.has(list)) {
      throw new SpError(0, list, `${list} has no library rows — reading them would merge another project's data`);
    }
    return this.http.getAll<T>(`${listPath(list)}/items?${buildQuery(q, this.withLibraryFilter)}`);
  }

  async one<T>(list: ListName, id: number, select?: readonly string[]): Promise<T | null> {
    const rows = await this.list<T>(list, { filter: `Id eq ${id}`, top: 1, ...(select ? { select } : {}) });
    return rows[0] ?? null;
  }

  /** Creates a row already stamped with this project. The caller cannot set another. */
  async create<T = { Id: number }>(list: ListName, fields: Record<string, unknown>): Promise<T> {
    const type = await this.types.of(list);
    const { ProjectId: _ignored, ...rest } = fields;
    return this.http.create<T>(`${listPath(list)}/items`, type, { ...rest, ProjectId: this.project.id });
  }

  /**
   * Updates a row, after checking it belongs to this project.
   *
   * The read costs a round trip and is worth it: an id from a stale cache or a
   * mistyped deep link would otherwise let one project write over another's row,
   * and nothing downstream would ever notice.
   */
  async update(list: ListName, id: number, fields: Record<string, unknown>): Promise<void> {
    const mine = await this.one<{ Id: number }>(list, id, ['Id']);
    if (!mine) {
      throw new SpError(404, `${list}(${id})`, `item ${id} is not in project ${this.project.code}`);
    }
    const type = await this.types.of(list);
    const { ProjectId: _ignored, ...rest } = fields;
    await this.http.update(`${listPath(list)}/items(${id})`, type, rest);
  }

  async recycle(list: ListName, id: number): Promise<void> {
    const mine = await this.one<{ Id: number }>(list, id, ['Id']);
    if (!mine) {
      throw new SpError(404, `${list}(${id})`, `item ${id} is not in project ${this.project.code}`);
    }
    await this.http.recycle(`${listPath(list)}/items(${id})`);
  }
}

/** The unscoped surface. Deliberately tiny: it can list projects and nothing else. */
export class SpClient {
  readonly types: EntityTypes;
  constructor(private readonly http: SpHttp) {
    this.types = new EntityTypes(http);
  }

  async projects(): Promise<ProjectRef[]> {
    const rows = await this.http.getAll<{ Id: number; ProjectCode?: string; Title?: string; ProjectStatus?: string }>(
      `${listPath(LIST.projects)}/items?$select=Id,ProjectCode,Title,ProjectStatus&$orderby=Title&$top=500`,
    );
    return rows.map((r) => ({ id: r.Id, code: r.ProjectCode ?? '', name: r.Title ?? '' }));
  }

  scope(project: ProjectRef): ProjectScope {
    return new ProjectScope(this.http, this.types, project);
  }
}
